<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'FileUploader',
})
</script>
<template>
  <div :class="bem()">
    <!-- 队列上传进度条 -->
    <div v-if="queueState.isActive" :class="bem('queue-progress')">
      <div :class="bem('queue-header')">
        <span :class="bem('queue-title')"> 队列上传中 </span>
        <span :class="bem('queue-stats')">
          {{ queueState.completed }}/{{ queueState.total }} 已完成
          <template v-if="queueState.failed > 0">
            ，<span :class="bem('queue-failed')">{{ queueState.failed }} 失败</span>
          </template>
        </span>
      </div>
      <Progress :percent="queueProgress" :status="queueProgressStatus" :stroke-color="{ from: '#108ee9', to: '#87d068' }" size="small" />
      <div :class="bem('queue-info')">
        <span>并发数: {{ MAX_CONCURRENT }}</span>
        <span>进行中: {{ queueState.uploading }}</span>
        <span>等待中: {{ queueState.pending }}</span>
      </div>
    </div>

    <Upload.Dragger
      v-model:fileList="fileList"
      :multiple="true"
      :custom-request="handleUpload"
      :before-upload="beforeUpload"
      @remove="handleRemove"
      :show-upload-list="{ showPreviewIcon: false, showRemoveIcon: true }"
      :disabled="queueState.isActive"
    >
      <CloudUploadOutlined :class="bem('icon')" />
      <div :class="bem('text')">
        {{ queueState.isActive ? '队列上传中，请等待...' : '点击或拖拽文件上传' }}
      </div>
      <div v-if="!queueState.isActive" :class="bem('hint')">选择超过 {{ QUEUE_THRESHOLD }} 个文件将启用队列上传</div>
    </Upload.Dragger>
  </div>
</template>

<script lang="ts" setup name="FileUploader">
import { CloudUploadOutlined } from '@ant-design/icons-vue'
import { createNamespace } from '@bd/vue3-utils'
import { message, Upload, Progress, type UploadFile } from 'ant-design-vue'
import { isCancel } from 'axios'
import { ref, reactive, computed, onUnmounted } from 'vue'
import { API_FILE_UPLOAD } from '@/api/file'

const [_, bem] = createNamespace('FileUploader')

const emits = defineEmits<{
  (e: 'add', file: ApiFile.Item): void
  (e: 'del', fileId: string): void
  (e: 'queueComplete', result: { success: number; failed: number }): void
}>()

/** 队列上传阈值：超过此数量启用队列上传 */
const QUEUE_THRESHOLD = 10
/** 最大并发上传数 */
const MAX_CONCURRENT = 6

/**
 * 使用 Map 管理每个文件 uid 对应的 AbortController
 */
const abortMap = new Map<string, AbortController>()

const fileList = ref<UploadFile[]>([])

/** 待上传队列 */
interface QueueItem {
  file: File
  uid: string
  options: any
}
const uploadQueue = ref<QueueItem[]>([])

/** 队列状态 */
const queueState = reactive({
  isActive: false, // 队列是否激活
  total: 0, // 总文件数
  completed: 0, // 已完成数
  failed: 0, // 失败数
  uploading: 0, // 正在上传数
  pending: 0 // 等待中数量
})

/** 队列整体进度 */
const queueProgress = computed(() => {
  if (queueState.total === 0) return 0
  return Math.round(((queueState.completed + queueState.failed) * 100) / queueState.total)
})

/** 队列进度条状态 */
const queueProgressStatus = computed(() => {
  if (queueState.failed > 0 && queueState.completed + queueState.failed === queueState.total) {
    return 'exception'
  }
  if (queueState.completed === queueState.total) {
    return 'success'
  }
  return 'active'
})

/** 批次文件收集器 */
let batchFiles: File[] = []
let batchTimer: ReturnType<typeof setTimeout> | null = null

/**
 * 计算上传进度（修复潜在 bug：total 为 0 时除 0 导致 NaN/Infinity）
 */
function calcPercent(loaded: number, total?: number) {
  if (!total || total <= 0) return 0
  return Math.round((loaded * 100) / total)
}

/**
 * 创建并登记 AbortController（单一职责：只负责创建与登记）
 */
function createAbort(uid: string) {
  const controller = new AbortController()
  abortMap.set(uid, controller)
  return controller
}

/**
 * 清理 AbortController（单一职责：只负责清理）
 */
function cleanupAbort(uid: string) {
  abortMap.delete(uid)
}

/**
 * 重置队列状态
 */
function resetQueueState() {
  queueState.isActive = false
  queueState.total = 0
  queueState.completed = 0
  queueState.failed = 0
  queueState.uploading = 0
  queueState.pending = 0
  uploadQueue.value = []
}

/**
 * 执行单个文件上传
 */
async function executeUpload(item: QueueItem): Promise<boolean> {
  const { file, uid, options } = item
  const { onSuccess, onProgress, onError } = options
  const controller = createAbort(uid)

  try {
    const data = await API_FILE_UPLOAD(
      { file },
      {
        signal: controller.signal,
        onUploadProgress({ loaded, total }) {
          onProgress?.({ percent: calcPercent(loaded, total) })
        }
      }
    )
    if (isCancel(data)) return false
    emits('add', data)
    onSuccess?.(data as any, file as any)
    return true
  } catch (err) {
    onError?.(err as any)
    return false
  } finally {
    cleanupAbort(uid)
  }
}

/**
 * 处理队列中的下一个文件
 */
async function processQueue() {
  // 检查是否还有待上传的文件且未达到并发上限
  while (uploadQueue.value.length > 0 && queueState.uploading < MAX_CONCURRENT) {
    const item = uploadQueue.value.shift()
    if (!item) break

    queueState.uploading++
    queueState.pending = uploadQueue.value.length

    // 异步执行上传，不等待
    executeUpload(item).then(success => {
      queueState.uploading--
      if (success) {
        queueState.completed++
      } else {
        queueState.failed++
      }
      queueState.pending = uploadQueue.value.length

      // 继续处理队列
      processQueue()

      // 检查队列是否完成
      if (queueState.uploading === 0 && uploadQueue.value.length === 0) {
        const result = { success: queueState.completed, failed: queueState.failed }
        if (queueState.failed === 0) {
          message.success(`队列上传完成，共 ${queueState.completed} 个文件`)
        } else {
          message.warning(`队列上传完成：${queueState.completed} 成功，${queueState.failed} 失败`)
        }
        emits('queueComplete', result)
        // 延迟重置状态，让用户看到最终结果
        setTimeout(resetQueueState, 2000)
      }
    })
  }
}

/**
 * 启动队列上传
 */
function startQueueUpload(items: QueueItem[]) {
  resetQueueState()
  queueState.isActive = true
  queueState.total = items.length
  queueState.pending = items.length
  uploadQueue.value = [...items]

  message.info(`已启用队列上传，共 ${items.length} 个文件，并发数 ${MAX_CONCURRENT}`)

  // 开始处理队列
  processQueue()
}

/**
 * 收集批次文件并决定是否启用队列
 */
function collectBatchFile(file: File, options: any) {
  batchFiles.push(file)

  // 清除之前的定时器
  if (batchTimer) {
    clearTimeout(batchTimer)
  }

  // 设置新的定时器，等待所有文件收集完成
  batchTimer = setTimeout(() => {
    const files = [...batchFiles]
    batchFiles = []
    batchTimer = null

    if (files.length > QUEUE_THRESHOLD) {
      // 启用队列上传
      const items: QueueItem[] = files.map((f, index) => ({
        file: f,
        uid: `queue-${Date.now()}-${index}`,
        options
      }))
      startQueueUpload(items)
    }
  }, 50)
}

/**
 * beforeUpload 钩子：收集批次文件
 */
function beforeUpload(file: File, fileListArg: File[]) {
  // 如果一次选择的文件数量超过阈值，阻止默认上传行为
  if (fileListArg.length > QUEUE_THRESHOLD) {
    // 收集文件用于队列上传
    collectBatchFile(file, {})
    return false
  }
  return true
}

/**
 * 自定义上传逻辑
 */
async function handleUpload(options: any) {
  const { file, onSuccess, onProgress, onError } = options
  const uid = (file as UploadFile).uid
  const controller = createAbort(uid)

  try {
    const data = await API_FILE_UPLOAD(
      { file: file as File },
      {
        signal: controller.signal,
        onUploadProgress({ loaded, total }) {
          onProgress?.({ percent: calcPercent(loaded, total) })
        }
      }
    )
    if (isCancel(data)) return
    emits('add', data)
    onSuccess?.(data as any, file as any)
    message.success('文件上传成功')
  } catch (err) {
    onError?.(err as any)
    message.error('文件上传失败')
  } finally {
    cleanupAbort(uid)
  }
}

/**
 * 移除文件时：如果仍在上传则中止
 */
function handleRemove(file: UploadFile) {
  const uid = file.uid
  const controller = abortMap.get(uid)
  if (controller) {
    controller.abort()
    cleanupAbort(uid)
  }
}

/**
 * 取消所有上传并清空队列
 */
function cancelAllUploads() {
  // 取消所有正在进行的上传
  abortMap.forEach(controller => {
    controller.abort()
  })
  abortMap.clear()

  // 清空批次收集器
  if (batchTimer) {
    clearTimeout(batchTimer)
    batchTimer = null
  }
  batchFiles = []

  // 重置队列状态
  resetQueueState()

  // 清空文件列表
  fileList.value = []
}

/**
 * 检查是否有正在上传的文件
 */
function hasUploadingFiles(): boolean {
  return abortMap.size > 0 || queueState.isActive
}

/**
 * 获取上传中的文件数量
 */
function getUploadingCount(): number {
  return abortMap.size + queueState.pending
}

/**
 * 组件销毁时清理
 */
onUnmounted(() => {
  cancelAllUploads()
})

// 暴露方法供父组件调用
defineExpose({
  hasUploadingFiles,
  getUploadingCount,
  cancelAllUploads
})
</script>

<style lang="less">
.file-uploader {
  position: relative;

  .ant-upload-list {
    max-height: 200px;
    overflow-y: auto;
  }

  .ant-upload-btn {
    padding: 60px 0 !important;
  }

  &__icon {
    color: @gray;
    font-size: 32px;
  }

  &__text {
    padding-top: @padding-sm;
  }

  &__hint {
    margin-top: 8px;
    color: @text-color-secondary;
    font-size: 12px;
  }

  &__queue-progress {
    margin-bottom: @margin-md;
    padding: @padding-sm;
    border: 1px solid #91d5ff;
    border-radius: @border-radius-base;
    background: linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%);
  }

  &__queue-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: @margin-sm;
  }

  &__queue-title {
    display: flex;
    gap: @margin-sm;
    align-items: center;
    color: #1890ff;
    font-weight: 600;
    font-size: 14px;

    .anticon {
      font-size: 16px;
    }
  }

  &__queue-stats {
    color: @text-color-secondary;
    font-size: 12px;
  }

  &__queue-failed {
    color: @warning-color;
    font-weight: 500;
  }

  &__queue-info {
    display: flex;
    gap: 24px;
    margin-top: @margin-xs;
    color: @text-color-secondary;
    font-size: 12px;

    span {
      display: flex;
      gap: 4px;
      align-items: center;

      &::before {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: @primary-color;
        content: '';
      }

      &:nth-child(2)::before {
        background: @success-color;
      }

      &:nth-child(3)::before {
        background: @warning-color;
      }
    }
  }
}
</style>
